import { Box, Checkbox, Image } from '@chakra-ui/react';
import Select, { components } from 'react-select';
import { find, isEmpty, upperFirst } from 'lodash';
import { useMemo, useRef, useState } from 'react';

import { ChevronDownIcon, CloseIcon, ChevronUpIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import SkeletonComponent from 'UiKit/skeleton';
import { TextRegular, TextSemiBold } from 'UiKit/typography';

import { reactSelectStyle } from './const';
import styles from './styles.module.css';
import './styles.css';

const MENU_PLACEMENT_THRESHOLD = 200 + 8;

const InputSelectComponent = ({
  label,
  placeholder,
  onChange,
  value,
  isDisabled,
  error,
  options = [],
  isRequired,
  margin,
  className,
  titleClassName,
  isMulti,
  field,
  isLoading,
  isCloseMenuOnSelect = true,
  isReadOnly,
  isFetching,
  minHeight = '42px',
  isSearchable = true,
  rightLabel,
  fontSize,
  menuPortalTarget,
  menuPosition,
  onMenuOpen,
}) => {
  const isError = !isEmpty(error);
  const isInputSettled = !isEmpty(value) || isReadOnly;
  const containerRef = useRef(null);
  const [menuPlacement, setMenuPlacement] = useState('bottom');

  const handleMenuOpen = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setMenuPlacement(spaceBelow < MENU_PLACEMENT_THRESHOLD ? 'top' : 'bottom');
    }
    if (onMenuOpen) onMenuOpen();
  };

  const selectStyles = useMemo(
    () => ({
      menuList: (provided) => ({ ...provided, ...reactSelectStyle.menuList }),
      control: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.control, minHeight }),
      ...(menuPortalTarget ? { menuPortal: (base) => ({ ...base, zIndex: 9999 }) } : {}),
    }),
    [minHeight, menuPortalTarget],
  );

  return (
    <Box
      margin={margin}
      className={`
        select-component__container
        ${isInputSettled ? 'select-component__container--selected' : 'select-component__container--empty'}
        ${className} ${styles['select-component__container']}
        ${isDisabled && styles['select-component__container--disabled']}
        ${isReadOnly && styles['select-component__container--readonly']}
        ${isError && 'select-component__container--error'}
      `}
      style={{
        ...(fontSize ? { '--custom-font-size': fontSize } : {}),
        ...(minHeight ? { '--custom-min-height': minHeight } : {}),
      }}
    >
      <Condition statement={label}>
        <Box className={styles['label__container']}>
          <TextSemiBold className={`${titleClassName} ${styles['text__title']}`}>
            {label}{' '}
            <Condition statement={isRequired}>
              <span>*</span>
            </Condition>
          </TextSemiBold>
          <Condition statement={rightLabel}>
            <Box className={styles['box__right-label']}>{rightLabel}</Box>
          </Condition>
        </Box>
      </Condition>

      <Condition statement={!isLoading && !isFetching} fallback={<SkeletonComponent height="42px" width="100%" />}>
        <Box
          ref={containerRef}
          position={'relative'}
          bg={isInputSettled ? 'var(--color-semantic-background)' : 'var(--color-white)'}
          className={`${styles['select-component__box-container']}`}
        >
          <Select
            components={{
              Control,
              Input,
              DropdownIndicator,
              ClearIndicator,
              MultiValue,
              MultiValueContainer,
              SingleValue,
              MultiValueRemove,
              Menu,
              Option: (props) => <Option isMulti={isMulti} selectedValue={value} {...props} />,
              IndicatorSeparator: null,
            }}
            onMenuOpen={handleMenuOpen}
            placeholder={placeholder}
            value={value}
            onChange={onChange ? onChange : (selectedOption) => field.onChange(selectedOption.value)}
            isDisabled={isDisabled || isReadOnly}
            isMulti={isMulti}
            options={options}
            hideSelectedOptions={false}
            closeMenuOnSelect={isCloseMenuOnSelect}
            isOptionSelected={true}
            isSearchable={isSearchable}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={selectStyles}
            menuPlacement={menuPlacement}
            {...field}
          />
        </Box>

        <Condition statement={isError}>
          <TextRegular className={styles['error__text']}>{error?.message}</TextRegular>
        </Condition>
      </Condition>
    </Box>
  );
};

const MemoizedInputSelectComponent = (props) => {
  return useMemo(
    () => <InputSelectComponent {...props} />,
    /* eslint-disable */
    [props?.value, props?.options, props?.isLoading, props?.error, props?.isReadOnly, props?.isRequired, props?.onChange, props?.field, props?.isDisabled],
  );
};

export default MemoizedInputSelectComponent;

const Control = ({ children, ...props }) => (
  <components.Control className={`input__control ${props.isDisabled ? 'input__control-disabled' : ''}`} {...props}>
    {children}
  </components.Control>
);

const Input = (props) => <components.Input className="input__field" {...props} />;

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator className="dropdown" {...props}>
      <Box style={{ pointerEvents: 'none' }}>
        <Condition
          statement={props.selectProps?.menuIsOpen && props.selectProps?.isSearchable}
          fallback={<ChevronDownIcon size="16px" color="var(--color-neutral40)" cursor="pointer" />}
        >
          <ChevronUpIcon size="16px" cursor="pointer" />
        </Condition>
      </Box>
    </components.DropdownIndicator>
  );
};

const ClearIndicator = (props) => {
  return (
    <components.ClearIndicator {...props}>
      <CloseIcon color="var(--color-semantic-text)" size="12px" cursor="pointer" />
    </components.ClearIndicator>
  );
};

const MultiValue = (props) => {
  return (
    <components.MultiValue {...props} className={`"selected-value__container--multiple" ${styles['selected-value__container--multiple']}`}>
      <TextSemiBold className="selected__value--text" dangerouslySetInnerHTML={{ __html: upperFirst(props.data.label) }} />
    </components.MultiValue>
  );
};

const SingleValue = (props) => {
  return (
    <components.SingleValue {...props} className={`selected-value__container--single`}>
      <TextRegular
        className={`selected__value--single--text ${props.isDisabled && 'selected__value--single--text-disabled'}`}
        dangerouslySetInnerHTML={{ __html: upperFirst(props.data.label) }}
      />
    </components.SingleValue>
  );
};

const Option = (props) => {
  const { data, innerRef, innerProps, isSelected, selectedValue, isMulti, isDisabled } = props;
  const isChecked = find(selectedValue, { value: data?.value });
  const image = data?.ecommerce?.logo;

  return (
    <Box ref={innerRef} {...innerProps} className="option__container" bg={isSelected ? 'var(--color-semantic-background)' : 'white'}>
      <Condition statement={isMulti}>
        <Checkbox size="lg" isChecked={isChecked} disabled={isDisabled} className="check-box-input-select" />
      </Condition>
      <Condition statement={image}>
        <Image src={image} alt="image" width="16px" height="16px" objectFit="contain" marginRight="8px" />
      </Condition>
      <TextRegular
        className={`option__text ${isDisabled ? styles['option__text-disabled'] : ''}`}
        dangerouslySetInnerHTML={{ __html: upperFirst(data?.label) }}
      />
      <Condition statement={data?.labelDescription}>
        <TextRegular className="option__text--description">{`(${data?.labelDescription})`}</TextRegular>
      </Condition>
    </Box>
  );
};

const MultiValueRemove = (props) => {
  return (
    <components.MultiValueRemove {...props} className="selected__value--icon">
      <CloseIcon color="var(--color-semantic-text)" size="12px" />
    </components.MultiValueRemove>
  );
};

const MultiValueContainer = (props) => {
  return <components.MultiValueContainer className="selected-multivalue__container" {...props} />;
};

const Menu = (props) => <components.Menu className={styles['menu__container']} {...props} />;
